import React, { Component, Fragment } from "react";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { InputSwitch } from "primereact/inputswitch";
import { confirmDialog } from "primereact/confirmdialog";
import { Tooltip } from "primereact/tooltip";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import moment from "moment";
import { Eye, Pencil, Trash2 } from "lucide-react";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { numberFilterMatchModeOptions, stringFilterMatchModeOptions, dateFilterTemplate, statusFilterMatchModeOptions } from "../Paginator";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import tariffApiService from "../../services/api/tariff/TariffApiService";
import { BillTariffModel, CreateBillTariffRequest, UpdateBillTariffRequest, ScaleTier } from "../../models/BillTariff";
import UtilityBillsTariffModal from "../../components/UtilityBillsTariff/UtilityBillsTariffModal";
import ViewUtilityBillsTariffModal from "../../components/UtilityBillsTariff/ViewUtilityBillsTariffModal";
import { DEFAULT_CURRENCY, AppConstants } from "../../models/AppConstants";
import { TariffCategory } from "../../models/TariffCategory";

interface UtilityBillsTariffState {
    billTariffs: BillTariffModel[];
    isLoading: boolean;
    totalRecords: number;
    showDialog: boolean;
    isEditMode: boolean;
    showViewDialog: boolean;
    selectedBillTariff: BillTariffModel | null;
    maxVersionsByBillTypeCode: Map<string, number>; // Map to store max version for each BillTypeCode
    isInitialLoad: boolean; // Track if this is the initial load to apply default filter
}

const tariffTypeOptions = [
    { label: "PERCENTAGE", value: "PERCENTAGE" },
    { label: "FIXED", value: "FIXED" },
    { label: "SCALE", value: "SCALE" },
];

const emptyScaleTier: ScaleTier = { minValue: 0, maxValue: 0, tariffAmount: 0 };

interface UtilityBillsTariffProps {
    merchantId?: string;
}

export default class UtilityBillsTariff extends Component<UtilityBillsTariffProps, UtilityBillsTariffState> {
    private toast: Toast | null = null;
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: UtilityBillsTariffProps) {
        super(props);

        this.state = {
            billTariffs: [],
            isLoading: false,
            totalRecords: 0,
            showDialog: false,
            isEditMode: false,
            showViewDialog: false,
            selectedBillTariff: null,
            maxVersionsByBillTypeCode: new Map<string, number>(),
            isInitialLoad: true,
        };
    }

    componentDidMount() {
        // Initial load will be triggered by ExtendedDataTable's onChange
    }

    // Calculate maximum version for each BillTypeCode
    calculateMaxVersions = (billTariffs: BillTariffModel[]): Map<string, number> => {
        const maxVersions = new Map<string, number>();

        billTariffs.forEach((tariff) => {
            const currentMax = maxVersions.get(tariff.billTypeCode) || 0;
            if (tariff.version > currentMax) {
                maxVersions.set(tariff.billTypeCode, tariff.version);
            }
        });

        return maxVersions;
    };

    // Check if a bill tariff has the maximum version
    isLatestVersion = (billTariff: BillTariffModel): boolean => {
        const maxVersion = this.state.maxVersionsByBillTypeCode.get(billTariff.billTypeCode);
        return maxVersion !== undefined && billTariff.version === maxVersion;
    };

    // Status filter template with dropdown
    statusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        // Determine current value: "true" or "false"
        let currentValue: string | null = null;
        if (options.value === "true" || options.value === true) {
            currentValue = "true";
        } else if (options.value === "false" || options.value === false) {
            currentValue = "false";
        } else {
            currentValue = null; // No selection
        }

        return (
            <Dropdown
                value={currentValue}
                options={[
                    { label: "Active", value: "true" },
                    { label: "Inactive", value: "false" },
                ]}
                onChange={(e: any) => {
                    options.filterApplyCallback(e.value);
                }}
                placeholder="Select Status"
                showClear
                className="p-column-filter"
            />
        );
    };

    // Load bill tariffs with server-side pagination, filtering, and sorting
    loadBillTariffsData = async (searchRequest: SearchRequestBaseModel) => {
        // Add filter for UtilityBillsTariff category (category = 2)
        const utilityBillsRequest = { ...searchRequest };

        // Check if tariffCategory filter already exists
        const existingCategoryFilter = utilityBillsRequest.filters.find(f => f.columnName === "TariffCategory");
        if (existingCategoryFilter) {
            existingCategoryFilter.value = TariffCategory.UtilityBillsTariff.toString();
        } else {
            utilityBillsRequest.filters.push({
                columnName: "TariffCategory",
                operator: "Equals",
                value: TariffCategory.UtilityBillsTariff.toString()
            });
        }

        if (this.state.isInitialLoad) {
            this.setState({ isInitialLoad: false });
        }

        this.setState({ isLoading: true });

        try {
            const result = await tariffApiService.getAllBillTariffsPaged(utilityBillsRequest);

            if (!result.success) {
                this.setState({ isLoading: false });
                this.toast?.show({ severity: "error", summary: "Error", detail: result.message, life: 3000 });
                return;
            }

            const billTariffs = result.data || [];
            const maxVersions = this.calculateMaxVersions(billTariffs);

            this.setState({
                billTariffs: billTariffs,
                totalRecords: result.totalCount || 0,
                maxVersionsByBillTypeCode: maxVersions,
                isLoading: false,
            });
        } catch (error) {
            this.setState({ isLoading: false });
            this.toast?.show({ severity: "error", summary: "Error", detail: "Failed to load utility bills tariffs", life: 3000 });
        }
    };

    openCreateDialog = () => {
        this.setState({
            showDialog: true,
            isEditMode: false,
            selectedBillTariff: null,
        });
    };

    openEditDialog = (billTariff: BillTariffModel) => {
        this.setState({
            showDialog: true,
            isEditMode: true,
            selectedBillTariff: billTariff,
        });
    };

    closeDialog = () => {
        this.setState({
            showDialog: false,
            isEditMode: false,
            selectedBillTariff: null,
        });
    };

    openViewDialog = (billTariff: BillTariffModel) => {
        this.setState({
            showViewDialog: true,
            selectedBillTariff: billTariff,
        });
    };

    closeViewDialog = () => {
        this.setState({
            showViewDialog: false,
            selectedBillTariff: null,
        });
    };

    // Form validation and operations moved to EditBillTariffModal component
    // All form-related methods have been removed

    handleDelete = (billTariff: BillTariffModel) => {
        confirmDialog({
            message: `Are you sure you want to delete Utility Bills Tariff for ${billTariff.billTypeCode}?`,
            header: "Confirm Delete",
            icon: "pi pi-exclamation-triangle",
            accept: async () => {
                this.setState({ isLoading: true });
                const result = await tariffApiService.deleteBillTariff(billTariff.id);

                if (!result.success) {
                    this.toast?.show({ severity: "error", summary: "Error", detail: result.message, life: 3000 });
                    this.setState({ isLoading: false });
                    return;
                }

                this.toast?.show({ severity: "success", summary: "Success", detail: "Utility Bills Tariff deleted successfully", life: 3000 });

                // Reset table to reload data with current filters/pagination
                if (this.dataTable) {
                    this.dataTable.reset();
                }
            },
        });
    };

    handleToggleStatus = async (billTariff: BillTariffModel) => {
        // Allow status toggle for any version (backend will handle validation)
        this.setState({ isLoading: true });
        const result = await tariffApiService.toggleBillTariffStatus(billTariff.id);

        if (!result.success) {
            this.toast?.show({ severity: "error", summary: "Error", detail: result.message, life: 3000 });
            this.setState({ isLoading: false });
            return;
        }

        this.toast?.show({
            severity: "success",
            summary: "Success",
            detail: "Utility Bills Tariff status toggled successfully",
            life: 3000,
        });

        // Reset table to reload data with current filters/pagination
        if (this.dataTable) {
            this.dataTable.reset();
        }
    };

    formatValueDisplay = (rowData: BillTariffModel): { text: string; tooltip?: string } => {
        if (rowData.tariffType === "SCALE") {
            try {
                const tiers = JSON.parse(rowData.value) as ScaleTier[];
                return {
                    text: `${tiers.length} tier(s)`,
                    tooltip: rowData.value,
                };
            } catch {
                return { text: rowData.value };
            }
        }
        return { text: rowData.value };
    };

    statusToggleTemplate = (rowData: BillTariffModel) => {
        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "5px" }} onClick={(e) => e.stopPropagation()}>
                <div style={{ flex: 1 }}>
                    <InputSwitch
                        checked={rowData.status}
                        onChange={(e) => {
                            e.stopPropagation();
                            this.handleToggleStatus(rowData);
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            </div>
        );
    };

    actionBodyTemplate = (rowData: BillTariffModel) => {
        return (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); this.openViewDialog(rowData); }}
                    className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                    title="View"
                >
                    <Eye size={15} />
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); this.openEditDialog(rowData); }}
                    className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                    title="Edit"
                >
                    <Pencil size={15} />
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); this.handleDelete(rowData); }}
                    className="p-1.5 rounded-md text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:text-danger-400 dark:hover:bg-danger-900/20 transition-colors"
                    title="Delete"
                >
                    <Trash2 size={15} />
                </button>
            </div>
        );
    };

    renderHeader = () => {
        return (
            <span className="p-input-icon-left" style={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                    type="button"
                    icon="pi pi-filter-slash"
                    label="Clear"
                    outlined
                    onClick={() => {
                        // Reset to initial state with Active filter
                        this.setState({ isInitialLoad: true });
                        this.dataTable!.reset();
                    }}
                />
                <Button label="Create Utility Bills Tariff" outlined icon="pi pi-plus" onClick={this.openCreateDialog} />
            </span>
        );
    };

    render() {

        return (
            <Fragment>
                <Toast ref={(el) => (this.toast = el)} position={"bottom-center"} baseZIndex={100000}></Toast>

                <ExtendedDataTable
                    ref={(ref) => (this.dataTable = ref)}
                    header={this.renderHeader}
                    loading={this.state.isLoading}
                    value={this.state.billTariffs}
                    onChange={this.loadBillTariffsData}
                    totalRecords={this.state.totalRecords}
                    defaultSortOrder={SortOrderType.Ascending}
                    defaultSortField="BillTypeCode"
                    onRowClick={(e) => {
                        // Open view dialog when row is clicked
                        // Action buttons use stopPropagation() to prevent this event
                        if (e.data) {
                            this.openViewDialog(e.data);
                        }
                    }}
                >
                    <Column
                        field="vendorName"
                        {...({ alwaysVisible: true } as any)}
                        header="Vendor"
                        body={(rowData: BillTariffModel) => {
                            return (
                                <p>{rowData.vendorName || "-"}</p>
                            );
                        }}
                        headerStyle={{ minWidth: "150px" }}
                    ></Column>
                    <Column
                        field="serviceName"
                        {...({ alwaysVisible: true } as any)}
                        header="Service"
                        body={(rowData: BillTariffModel) => {
                            const displayText = rowData.serviceName
                                ? (rowData.serviceCode ? `${rowData.serviceName} (${rowData.serviceCode})` : rowData.serviceName)
                                : (rowData.serviceCode || rowData.billTypeCode || "-");
                            return (
                                <p>{displayText}</p>
                            );
                        }}
                        headerStyle={{ minWidth: "150px" }}
                    ></Column>
                    <Column
                        field="billTypeCode"
                        {...({ alwaysVisible: true } as any)}
                        header="Bill Type Code"
                        filterMatchModeOptions={stringFilterMatchModeOptions}
                        filterMatchMode="Equals"
                        body={(rowData: BillTariffModel) => {
                            return (
                                <p>{rowData.billTypeCode}</p>
                            );
                        }}
                        headerStyle={{ minWidth: "200px" }}
                    ></Column>
                    <Column
                        field="tariffType"
                        header="Tariff Type"
                        body={(rowData: BillTariffModel) => {
                            return (
                                <p>{rowData.tariffType}</p>
                            );
                        }}
                        headerStyle={{ minWidth: "120px" }}
                    ></Column>
                    <Column
                        field="value"
                        header="Value"
                        body={(rowData: BillTariffModel) => {
                            const display = this.formatValueDisplay(rowData);
                            if (display.tooltip) {
                                const tooltipId = `value-tooltip-${rowData.id}`;
                                return (
                                    <>
                                        <p
                                            className={tooltipId}
                                            style={{ cursor: "pointer", textDecoration: "underline dotted" }}
                                        >
                                            {display.text}
                                        </p>
                                        <Tooltip target={`.${tooltipId}`} content={display.tooltip} position="top" />
                                    </>
                                );
                            }
                            return (
                                <p>{display.text}</p>
                            );
                        }}
                        headerStyle={{ minWidth: "100px" }}
                    ></Column>
                    <Column
                        field="currency"
                        header="Currency"
                        body={(rowData: BillTariffModel) => {
                            return (
                                <p>{rowData.currency || "-"}</p>
                            );
                        }}
                        sortable={false}
                        headerStyle={{ minWidth: "100px" }}
                    ></Column>
                    <Column
                        field="version"
                        header="Version"
                        body={(rowData: BillTariffModel) => {
                            return (
                                <p>{rowData.version}</p>
                            );
                        }}
                        headerStyle={{ minWidth: "100px" }}
                    ></Column>
                    <Column
                        field="effectiveFrom"
                        header="Effective From"
                        body={(rowData: BillTariffModel) => (
                            <p>{moment(rowData.effectiveFrom).format("DD/MM/YYYY")}</p>
                        )}
                        dataType="date"
                        filterElement={dateFilterTemplate}
                        filterMatchModeOptions={numberFilterMatchModeOptions}
                        filterMatchMode="Equals"
                        headerStyle={{ minWidth: "200px" }}
                    ></Column>
                    <Column
                        field="effectiveTo"
                        header="Effective To"
                        body={(rowData: BillTariffModel) =>
                            rowData.effectiveTo ? <p>{moment(rowData.effectiveTo).format("DD/MM/YYYY")}</p> : <p>-</p>
                        }
                        headerStyle={{ minWidth: "120px" }}
                    ></Column>
                    <Column
                        field="createdTimeStamp"
                        header="Created On"
                        body={(rowData: BillTariffModel) => (
                            <p>{moment(rowData.createdTimeStamp).isAfter(moment.utc("0001-01-01")) ? moment.utc(rowData.createdTimeStamp).local().format("DD/MM/YYYY HH:mm:ss") : "-"}</p>
                        )}
                        headerStyle={{ minWidth: "120px" }}
                    ></Column>
                    <Column
                        field="modifiedTimeStamp"
                        header="Modified On"
                        body={(rowData: BillTariffModel) => (
                            <p>{rowData.modifiedTimeStamp && moment(rowData.modifiedTimeStamp).isAfter(moment.utc("0001-01-01")) ? moment.utc(rowData.modifiedTimeStamp).local().format("DD/MM/YYYY HH:mm:ss") : "-"}</p>
                        )}
                        headerStyle={{ minWidth: "150px" }}
                    ></Column>
                    <Column
                        field="status"
                        header="Status"
                        body={this.statusToggleTemplate}
                        filterMatchModeOptions={statusFilterMatchModeOptions}
                        filterElement={this.statusFilterTemplate}
                        filterMatchMode="Equals"
                    ></Column>
                    <Column
                        body={this.actionBodyTemplate}
                        headerStyle={{ width: "12em", textAlign: "center" }}
                        bodyStyle={{ textAlign: "center", overflow: "visible" }}
                    />
                </ExtendedDataTable>


                <UtilityBillsTariffModal
                    visible={this.state.showDialog}
                    isEditMode={this.state.isEditMode}
                    selectedBillTariff={this.state.selectedBillTariff}
                    onHide={this.closeDialog}
                    onSuccess={() => {
                        // Reset table to reload data with current filters/pagination
                        if (this.dataTable) {
                            this.dataTable.reset();
                        }
                    }}
                />

                <ViewUtilityBillsTariffModal
                    visible={this.state.showViewDialog}
                    billTariff={this.state.selectedBillTariff}
                    onHide={this.closeViewDialog}
                />
            </Fragment>
        );
    }
}

